import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

export type ImageGeneratorRegenView = "front" | "back";

export type ImageGeneratorRegenSubmitPayload = {
  additionalPrompt: string;
  views: ImageGeneratorRegenView[];
};

export type ImageGeneratorRegenComposerHandle = {
  submit: () => boolean;
};

type Props = {
  open: boolean;
  outputType: "Style" | "Pattern";
  /** Number of generations already in this session chain. */
  generationCount: number;
  disabled?: boolean;
  initialViews?: ImageGeneratorRegenView[];
  resetKey?: number;
  onSubmit: (payload: ImageGeneratorRegenSubmitPayload) => void;
  onCancel: () => void;
};

/** Prompt is always optional — empty note = blind regen (print / full look / denim). */
const regenPromptCopy = (generationCount: number) => {
  const nextGeneration = generationCount + 1;
  if (nextGeneration === 3) {
    return {
      title: "What should we adjust?",
      placeholder: "Describe what to change… (optional)",
    };
  }
  if (generationCount >= 5) {
    return {
      title:
        "Final take for now - happy with this one, or want to relook at the brief? Definitely want to nail it the next time!",
      placeholder: "Describe what to change… (optional)",
    };
  }
  if (nextGeneration === 2) {
    return {
      title: "Sure, let’s have another go!",
      placeholder: "Anything to tweak? (optional)",
    };
  }
  return {
    title: "Enter Input (optional)",
    placeholder: "Describe what to change… (optional)",
  };
};

const ImageGeneratorRegenComposer = forwardRef<
  ImageGeneratorRegenComposerHandle,
  Props
>(
  (
    {
      open,
      outputType,
      generationCount,
      disabled,
      initialViews = [],
      resetKey = 0,
      onSubmit,
      onCancel,
    },
    ref
  ) => {
    const [prompt, setPrompt] = useState("");
    const [front, setFront] = useState(false);
    const [back, setBack] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const showViews = outputType === "Style";
    const copy = regenPromptCopy(generationCount);

    const views: ImageGeneratorRegenView[] = [
      ...(front ? (["front"] as const) : []),
      ...(back ? (["back"] as const) : []),
    ];

    const handleSubmit = () => {
      if (disabled) {
        return false;
      }
      onSubmit({
        additionalPrompt: prompt.trim(),
        views,
      });
      return true;
    };

    useImperativeHandle(ref, () => ({ submit: handleSubmit }), [
      disabled,
      onSubmit,
      prompt,
      views,
    ]);

    useEffect(() => {
      if (!open) {
        return;
      }
      setPrompt("");
      setFront(initialViews.includes("front"));
      setBack(initialViews.includes("back"));
      const timer = window.setTimeout(() => {
        textareaRef.current?.focus();
        rootRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 50);
      return () => window.clearTimeout(timer);
    }, [open, resetKey, initialViews]);

    if (!open) {
      return null;
    }

    const viewChipClassName = (selected: boolean) =>
      [
        "inline-flex h-8 w-[92px] shrink-0 items-center justify-center rounded-[100px] border border-[#737373] px-4 text-sm font-normal leading-none transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        selected
          ? "bg-[#F7DCD3] text-secondary"
          : "bg-white text-secondary hover:bg-gray",
      ].join(" ");

    return (
      <div ref={rootRef} className="w-full">
        <p className="mb-2 text-sm font-normal text-secondary">{copy.title}</p>
        <div className="flex min-h-[88px] overflow-hidden rounded-xl border border-[#737373] bg-white">
          {showViews && (
            <div className="flex w-[132px] shrink-0 flex-col items-start gap-2 border-r border-[#E2E2E2] px-3 py-3">
              <p className="whitespace-nowrap text-sm font-normal text-secondary">
                Select Image
              </p>
              <button
                type="button"
                disabled={disabled}
                aria-pressed={front}
                onClick={() => setFront(value => !value)}
                className={viewChipClassName(front)}
              >
                Front
              </button>
              <button
                type="button"
                disabled={disabled}
                aria-pressed={back}
                onClick={() => setBack(value => !value)}
                className={viewChipClassName(back)}
              >
                Back
              </button>
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={prompt}
            disabled={disabled}
            placeholder={copy.placeholder}
            onChange={event => setPrompt(event.target.value)}
            onKeyDown={event => {
              if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                event.preventDefault();
                handleSubmit();
              }
              if (event.key === "Escape") {
                event.preventDefault();
                onCancel();
              }
            }}
            className="min-h-[88px] w-full resize-none bg-transparent px-4 py-3 text-sm font-normal text-secondary placeholder:text-tertiary focus:outline-none disabled:opacity-60"
          />
        </div>
      </div>
    );
  }
);

ImageGeneratorRegenComposer.displayName = "ImageGeneratorRegenComposer";

export default ImageGeneratorRegenComposer;
